/**
 * Shopify Cart Ajax API helpers — modernized to native fetch.
 *
 * Replaces the legacy jQuery-AJAX ep-shopify-api.js. Aligns with
 * Shopify Dawn's approach (native fetch, no jQuery dependency) while
 * preserving the IIFE callers' contract:
 *
 *   Shopify.addItemFromForm(formEl, success, error)
 *   Shopify.getCart(callback)
 *   Shopify.addItem(variant_id, quantity, callback)
 *   Shopify.addJson(addJson, callback)
 *   Shopify.changeItem(variant_id, quantity, callback)
 *   Shopify.changeLine(line_id, quantity, callback)
 *   Shopify.removeItem(variant_id, callback)
 *   Shopify.clearCart(callback)
 *   Shopify.updateCartItems(itemUpdates, callback, errorCallback)
 *   Shopify.updateCartNote(note, callback)
 *   Shopify.getProduct(handle, callback)
 *   Shopify.queue / Shopify.processQueue
 *
 * Error-callback contract preserved: callers (notably
 * common-products-controller.itemErrorCallback) read `.responseText`,
 * `.status`, `.statusText` off the first argument, so the fetch wrapper
 * builds a structurally-compatible object before invoking the callback.
 *
 * Cross-module compat shim — `window.Shopify.*` retained for IIFE
 * consumers (cart, line-item, products controllers + application
 * hem-product getProduct call). Drop in Phase 13.
 *
 * Migrated from theme/js/plugins/ep-shopify-api.js (IIFE → ESM, Phase 11).
 *
 * Source path: theme/js/plugins/ep-shopify-api.esm.js
 * Built name : ./ep-shopify-api.js (sibling imports must use the built name)
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 *
 * [ShopifyApi]
 */

import { log } from './log.js';
import { unwrapJQuery } from './dom.js';

const Shopify = window.Shopify = window.Shopify || {};

// Structurally compatible with the jQuery jqXHR shape that legacy
// callbacks read off (`.responseText`, `.status`, `.statusText`).
function makeXhrLike(response, text) {
  return {
    responseText: text,
    status: response ? response.status : 0,
    statusText: response ? response.statusText : '',
  };
}

function postJsonForm(url, body, success, error) {
  const headers = {
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
  };

  let init;
  if (body instanceof FormData) {
    init = { method: 'POST', headers: headers, body: body };
  } else if (typeof body === 'string') {
    headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
    init = { method: 'POST', headers: headers, body: body };
  } else {
    headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
    init = { method: 'POST', headers: headers, body: new URLSearchParams(body || {}).toString() };
  }
  init.credentials = 'same-origin';

  return runFetch(url, init, success, error);
}

// Dawn-aligned: cart endpoints that take nested objects (notably
// /cart/update.js with `updates: { variantId: qty }`) need a JSON body,
// not URL-encoded. URLSearchParams would stringify the nested object
// as `[object Object]`; jQuery's $.param does deep encoding but native
// URLSearchParams doesn't. Send JSON instead.
function postJson(url, payload, success, error) {
  return runFetch(url, {
    method: 'POST',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  }, success, error);
}

function runFetch(url, init, success, error) {
  return fetch(url, init)
    .then((response) =>
      response.text().then((text) => {
        let data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { /* leave null */ }
        if (response.ok) {
          if (typeof success === 'function') return success(data, response);
        } else {
          if (typeof error === 'function') return error(makeXhrLike(response, text), response.statusText);
          Shopify.onError(makeXhrLike(response, text), response.statusText);
        }
      })
    )
    .catch((err) => {
      log('[Shopify API] Network error', err);
      if (typeof error === 'function') return error(makeXhrLike(null, ''), 'error');
      Shopify.onError(makeXhrLike(null, ''), 'error');
    });
}

function getJson(url, callback) {
  return fetch(url, {
    headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
    credentials: 'same-origin',
  })
    .then((response) => response.json())
    .then((data) => {
      if (typeof callback === 'function') callback(data);
      return data;
    })
    .catch((err) => {
      log('[Shopify API] GET failed', url, err);
      Shopify.onError(makeXhrLike(null, ''), 'error');
    });
}

// ─── Default error / event handlers ───────────────────────────────────────

Shopify.onProduct = Shopify.onProduct || function (product) { return product; };
Shopify.onCartUpdate = Shopify.onCartUpdate || function (cart) { log('[Shopify API] cart updated', cart); };
Shopify.onItemAdded = Shopify.onItemAdded || function (line_item) { log('[Shopify API] item added', line_item); };
Shopify.onError = Shopify.onError || function (xhr) {
  let data = null;
  try { data = JSON.parse(xhr.responseText); } catch (e) { /* */ }
  if (data && data.message) {
    log('[Shopify API] ' + data.message + ' (' + data.status + '): ' + data.description);
  } else {
    log('[Shopify API] error: ' + (xhr.statusText || 'unknown'));
  }
};

// ─── Cart endpoints (POST) ────────────────────────────────────────────────

Shopify.addItem = function (variant_id, quantity, callback) {
  quantity = quantity || 1;
  return postJsonForm(
    '/cart/add.js',
    'quantity=' + quantity + '&id=' + variant_id,
    (line_item) => {
      if (typeof callback === 'function') callback(line_item);
      else Shopify.onItemAdded(line_item);
    }
  );
};

Shopify.addJson = function (addJson, callback) {
  return postJsonForm(
    '/cart/add.js',
    new URLSearchParams(addJson).toString(),
    (line_item) => {
      if (typeof callback === 'function') callback(line_item);
      else Shopify.onItemAdded(line_item);
    }
  );
};

Shopify.addItemFromForm = function (form, callback, errorCallback) {
  // Caller may pass a jQuery collection or a DOM form.
  const formEl = unwrapJQuery(form);
  const data = formEl ? new FormData(formEl) : new FormData();
  return postJsonForm(
    '/cart/add.js',
    data,
    (line_item) => {
      if (typeof callback === 'function') callback(line_item, form);
      else Shopify.onItemAdded(line_item, form);
    },
    (xhrLike, textStatus) => {
      if (typeof errorCallback === 'function') errorCallback(xhrLike, textStatus, form);
      else Shopify.onError(xhrLike, textStatus, form);
    }
  );
};

Shopify.getCart = function (callback) {
  return getJson('/cart.js', (cart) => {
    if (typeof callback === 'function') callback(cart);
    else Shopify.onCartUpdate(cart);
  });
};

Shopify.getProduct = function (handle, callback) {
  return getJson('/products/' + handle + '.js', (product) => {
    if (typeof callback === 'function') callback(product);
    else Shopify.onProduct(product);
  });
};

Shopify.changeItem = function (variant_id, quantity, callback) {
  return postJsonForm(
    '/cart/change.js',
    'quantity=' + quantity + '&id=' + variant_id,
    (cart) => {
      if (typeof callback === 'function') callback(cart);
      else Shopify.onCartUpdate(cart);
    }
  );
};

Shopify.changeLine = function (line_id, quantity, callback) {
  return postJsonForm(
    '/cart/change.js',
    'line=' + line_id + '&quantity=' + quantity,
    (cart) => {
      if (typeof callback === 'function') callback(cart);
      else Shopify.onCartUpdate(cart);
    }
  );
};

Shopify.removeItem = function (variant_id, callback) {
  return postJsonForm(
    '/cart/change.js',
    'quantity=0&id=' + variant_id,
    (cart) => {
      if (typeof callback === 'function') callback(cart);
      else Shopify.onCartUpdate(cart);
    }
  );
};

Shopify.clearCart = function (callback) {
  return postJsonForm(
    '/cart/clear.js',
    '',
    (cart) => {
      if (typeof callback === 'function') callback(cart);
    },
    (xhrLike) => {
      log('[Shopify API] clearCart failed', xhrLike.status, xhrLike.statusText);
    }
  );
};

Shopify.updateCartItems = function (itemUpdates, callback, errorCallback) {
  return postJson(
    '/cart/update.js',
    { updates: itemUpdates },
    (cart) => {
      if (typeof callback === 'function') callback(cart);
      else Shopify.onCartUpdate(cart);
    },
    (xhrLike, textStatus) => {
      if (typeof errorCallback === 'function') errorCallback(xhrLike, textStatus);
      else Shopify.onError(xhrLike, textStatus);
    }
  );
};

Shopify.updateCartNote = function (note, callback) {
  return postJsonForm(
    '/cart/update.js',
    'note=' + encodeURIComponent(note == null ? '' : String(note).trim()),
    (cart) => {
      if (typeof callback === 'function') callback(cart);
      else Shopify.onCartUpdate(cart);
    }
  );
};

// ─── Add-item queue (legacy multi-add helper) ─────────────────────────────

Shopify.queue = Shopify.queue || [];

Shopify.processQueue = function () {
  if (Shopify.queue.length) {
    const request = Shopify.queue.shift();
    Shopify.addItem(request.variantId, request.quantity, Shopify.moveAlong);
  } else {
    document.location.href = '/cart';
  }
};

log('[ShopifyApi] registered');
